import React, { useContext, useEffect, useState, useCallback } from "react";
import { AppContext } from "../../../../appContext";
import { DashboardContext } from "../../DashboardContext";
import { Spinner } from "../../../../components/spinner/Spinner";
import { ConfirmationPopup } from "../../../../components/confirmationPopup/ConfirmationPopup";
import { getTaskItems, postTaskItem, putTaskItem, deleteTaskItem } from "../../../../api/taskApi";
import { ITicket, ISession } from "../../../../types/dashboard";
import { CustomSelect } from "./CustomSelect";
import { getFutureSessions } from "../../../../api/sessionApi";
import { addTaskReloadListener, removeTaskReloadListener, triggerTaskReload, triggerCalendarReload, addTaskCheckedListener, removeTaskCheckedListener, triggerTaskCheckedUpdate } from "../../../../utils/dataSyncTriggers";
import "./TasksView.css";
import TrashIcon from '../../../../assets/notesEditor/trash.svg';

interface ITaskItemData extends ITicket {
    tags?: string[];
    hasDate: boolean;
    description?: string;
    client?: string;
    sessionId?: string;
    checked?: boolean;
}

export const TasksView = () => {
    const { translate, user } = useContext(AppContext);
    const { selectedClient } = useContext(DashboardContext);
    const [tasks, setTasks] = useState<ITaskItemData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingTask, setEditingTask] = useState<{ id: string; data: ITaskItemData } | null>(null);
    const [filterTag, setFilterTag] = useState<string>('');
    const [filterByClient, setFilterByClient] = useState(false);
    const [deleteConfirmTask, setDeleteConfirmTask] = useState<string | null>(null);
    const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
    const [tasksWithToggle, setTasksWithToggle] = useState<Set<string>>(new Set());

    const loadTasks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getTaskItems();
            if (!res) {
                throw new Error('No response from server');
            }
            const data = await res.json();
            
            let dataArray: any[];
            const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
            if (Array.isArray(data)) {
                dataArray = data;
            } else {
                dataArray = Object.entries(data)
                    .filter(([id]) => !reservedKeys.includes(id))
                    .map(([id, taskData]) => ({ [id]: taskData }));
            }
            
            const convertedTasks = dataArray.map((item: any) => {
                const id = Object.keys(item)[0];
                if (reservedKeys.includes(id)) return null;
                const taskData = item[id];
                if (!taskData || typeof taskData !== 'object') return null;
                const startDate = taskData.Datetime 
                    ? new Date(typeof taskData.Datetime === 'number' ? taskData.Datetime * 1000 : taskData.Datetime)
                    : null;
                const endDate = taskData.end ? new Date(taskData.end) : startDate;
                
                return {
                    ...taskData,
                    id,
                    start: startDate,
                    end: endDate,
                    type: 'task',
                    hasDate: taskData.hasDate !== undefined ? taskData.hasDate : !!startDate,
                    tags: taskData.tags || [],
                    description: taskData.text || taskData.body || taskData.description || '',
                    client: taskData.client,
                    sessionId: taskData.sessionId,
                    checked: taskData.checked || false,
                    original: taskData
                };
            });
            
            setTasks(convertedTasks.filter(Boolean));
        } catch (err) {
            console.error('Failed to load tasks:', err);
            setError(translate('tasks_load_error') || 'Failed to load tasks');
        } finally {
            setLoading(false);
        }
    }, [translate]);

    useEffect(() => {
        if (user) {
            loadTasks();
        }
    }, [user, loadTasks]);

    useEffect(() => {
        addTaskReloadListener(loadTasks);
        
        return () => {
            removeTaskReloadListener(loadTasks);
        };
    }, [loadTasks]);

    useEffect(() => {
        const handleTaskCheckedUpdate = (taskId: string, checked: boolean) => {
            setTasks(prev => prev.map(t => t.id === taskId ? { ...t, checked } : t));
        };
        addTaskCheckedListener(handleTaskCheckedUpdate);
        return () => {
            removeTaskCheckedListener(handleTaskCheckedUpdate);
        };
    }, []);

    const handleAddTask = () => {
        setEditingTask(null);
        setShowAddModal(true);
    };

    const handleEditTask = (task: ITaskItemData) => {
        setEditingTask({ id: task.id!, data: task });
        setShowAddModal(true);
    };

    const handleDeleteTask = async () => {
        if (!deleteConfirmTask) return;
        
        try {
            await deleteTaskItem(deleteConfirmTask);
            setDeleteConfirmTask(null);
            await loadTasks();
            triggerTaskReload();
            triggerCalendarReload();
        } catch (err) {
            console.error('Failed to delete task:', err);
            setError(translate('tasks_delete_error') || 'Failed to delete task');
            setDeleteConfirmTask(null);
        }
    };

    const handleSaveTask = async (taskData: ITaskItemData) => {
        try {
            const apiPayload: any = {
                title: taskData.title,
                type: 'task',
                tags: taskData.tags || [],
                text: taskData.description || '',
                hasDate: taskData.hasDate,
                client: taskData.client,
                sessionId: taskData.sessionId
            };
            
            if (taskData.hasDate && taskData.start) {
                apiPayload.Datetime = Math.floor(taskData.start.valueOf() / 1000);
            }
            
            console.log('TasksView handleSaveTask:', { taskData, apiPayload, editingTaskId: editingTask?.id });
            
            if (editingTask?.id) {
                const response = await putTaskItem(apiPayload, editingTask.id);
                const result = await response?.json();
                console.log('TasksView putTaskItem response:', result);
            } else {
                const response = await postTaskItem(apiPayload);
                const result = await response?.json();
                console.log('TasksView postTaskItem response:', result);
            }
            
            setShowAddModal(false);
            setEditingTask(null);
            await loadTasks();
            triggerTaskReload();
            triggerCalendarReload();
        } catch (err) {
            console.error('Failed to save task:', err);
            setError(translate('tasks_save_error') || 'Failed to save task');
        }
    };

    const toggleTaskChecked = async (task: ITaskItemData) => {
        const newChecked = !task.checked;
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, checked: newChecked } : t));
        triggerTaskCheckedUpdate(task.id!, newChecked);
        try {
            const apiPayload = {
                title: task.title,
                text: task.description || '',
                tags: task.tags || [],
                Datetime: task.hasDate && task.start ? Math.floor(task.start.valueOf() / 1000) : undefined,
                hasDate: task.hasDate,
                checked: newChecked,
                client: task.client,
                sessionId: task.sessionId,
                type: 'task'
            };
            await putTaskItem(apiPayload as any, task.id!);
        } catch (e) {
            console.error('Failed to update task:', e);
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, checked: !newChecked } : t));
            triggerTaskCheckedUpdate(task.id!, !newChecked);
        }
    };

    const allTags = Array.from(new Set(tasks.flatMap(t => t.tags || [])));
    const tasksAfterClientFilter = filterByClient && selectedClient?.id
        ? tasks.filter(t => t.client === selectedClient.id)
        : tasks;
    const filteredTasks = filterTag
        ? tasksAfterClientFilter.filter(t => t.tags?.includes(filterTag))
        : tasksAfterClientFilter;

    return (
        <div className="TasksView">
            <div className="TasksView_header">
                <div className="TasksView_title_wrapper">
                    <div className="TasksView_title">{translate('tasks_title')}</div>
                    {selectedClient && (
                        <div 
                            className={`TasksView_filter_icon ${!filterByClient ? 'TasksView_filter_icon--active' : ''}`}
                            onClick={() => setFilterByClient(!filterByClient)}
                            title={filterByClient 
                                ? translate('filter_show_client_tasks')?.replace('{name}', selectedClient.name || '')
                                : translate('filter_show_all_tasks')
                            }
                        />
                    )}
                </div>
                <div className="TasksView_header_actions">
                    {allTags.length > 0 && (
                        <CustomSelect
                            className="TasksView_tagFilter"
                            value={filterTag}
                            onChange={setFilterTag}
                            options={[
                                { value: '', label: translate('tasks_all_tags') },
                                ...allTags.map(tag => ({ value: tag, label: tag }))
                            ]}
                        />
                    )}
                    <div className="TasksView_addWrapper" onClick={handleAddTask}>
                        <div className="TasksView_addIcon"></div>
                        <span className="TasksView_addText">{translate('tasks_add_new')}</span>
                    </div>
                </div>
            </div>

            <div className="TasksView_main">
                {loading && (
                    <div className="TasksView_loading">
                        <Spinner />
                    </div>
                )}

                {!loading && filteredTasks.length === 0 && (
                    <div className="TasksView_empty">
                        {translate('tasks_empty')}
                    </div>
                )}

                {!loading && !error && filteredTasks.length > 0 && (
                    <div className="TasksView_list">
                        {filteredTasks.map(task => {
                            const isDescExpanded = expandedDescriptions.has(task.id!);
                            const hasDescription = task.description && task.description.trim().length > 0;
                            
                            const checkDescriptionHeight = (element: HTMLDivElement | null) => {
                                if (element && task.id && !isDescExpanded) {
                                    const isOverflowing = element.scrollHeight > element.clientHeight;
                                    setTasksWithToggle(prev => {
                                        const hasTask = prev.has(task.id!);
                                        
                                        if (isOverflowing && !hasTask) {
                                            const newSet = new Set(prev);
                                            newSet.add(task.id!);
                                            return newSet;
                                        } else if (!isOverflowing && hasTask) {
                                            const newSet = new Set(prev);
                                            newSet.delete(task.id!);
                                            return newSet;
                                        }
                                        
                                        return prev;
                                    });
                                }
                            };
                            
                            return (
                                <div key={task.id} className={`TasksView_task ${task.checked ? 'TasksView_task--checked' : ''}`}>
                                    <div 
                                        className={`TasksView_task_checkbox ${task.checked ? 'TasksView_task_checkbox--checked' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleTaskChecked(task);
                                        }}
                                    />
                                    <div className="TasksView_task_content" onClick={() => handleEditTask(task)}>
                                        <div className="TasksView_task_header">
                                            <div className={`TasksView_task_title ${task.checked ? 'TasksView_task_title--checked' : ''}`}>{task.title}</div>
                                            {task.hasDate && task.start && (
                                                <div className="TasksView_task_date">
                                                    {task.start.toLocaleDateString()} {task.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            )}
                                        </div>
                                        {hasDescription && (
                                            <div className="TasksView_task_description_wrapper">
                                                <div 
                                                    ref={checkDescriptionHeight}
                                                    className={`TasksView_task_description ${isDescExpanded ? 'expanded' : 'collapsed'}`}
                                                >
                                                    {task.description}
                                                </div>
                                                {tasksWithToggle.has(task.id!) && (
                                                    <button 
                                                        className="TasksView_task_toggle"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedDescriptions(prev => {
                                                                const newSet = new Set(prev);
                                                                if (newSet.has(task.id!)) {
                                                                    newSet.delete(task.id!);
                                                                } else {
                                                                    newSet.add(task.id!);
                                                                }
                                                                return newSet;
                                                            });
                                                        }}
                                                    >
                                                        {isDescExpanded ? translate('common_show_less') : translate('common_show_more')}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {task.tags && task.tags.length > 0 && (
                                            <div className="TasksView_task_tags">
                                                {task.tags.map(tag => (
                                                    <span key={tag} className="TasksView_task_tag">{tag}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {showAddModal && (
                <TaskEditModal
                    task={editingTask?.data}
                    taskId={editingTask?.id}
                    initialBoundToClient={!editingTask && filterByClient && !!selectedClient?.id}
                    onSave={handleSaveTask}
                    onDelete={editingTask?.id ? () => setDeleteConfirmTask(editingTask.id) : undefined}
                    onClose={() => {
                        setShowAddModal(false);
                        setEditingTask(null);
                    }}
                />
            )}

            {deleteConfirmTask && (
                <ConfirmationPopup
                    message={translate('tasks_confirm_delete')}
                    confirmText={translate('common_delete')}
                    cancelText={translate('common_cancel')}
                    onConfirm={handleDeleteTask}
                    onCancel={() => setDeleteConfirmTask(null)}
                    confirmButtonStyle="danger"
                />
            )}
        </div>
    );
};

interface ITaskEditModalProps {
    task?: ITaskItemData;
    taskId?: string;
    initialBoundToClient?: boolean;
    onSave: (task: ITaskItemData) => void;
    onDelete?: () => void;
    onClose: () => void;
}

const TaskEditModal = ({ task, taskId, initialBoundToClient, onSave, onDelete, onClose }: ITaskEditModalProps) => {
    const { translate } = useContext(AppContext);
    const dashboardContext = useContext(DashboardContext);
    const { selectedClient } = dashboardContext || {};
    const [title, setTitle] = useState(task?.title || '');
    const [description, setDescription] = useState(task?.description || '');
    const [hasDate, setHasDate] = useState(task ? task.hasDate : true);
    const [date, setDate] = useState(task?.start ? new Date(task.start) : new Date());
    const [tagInput, setTagInput] = useState('');
    const [tags, setTags] = useState<string[]>(task?.tags || []);
    const [boundToClient, setBoundToClient] = useState(task ? !!task.client : !!initialBoundToClient);
    const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(task?.sessionId);
    const [futureSessions, setFutureSessions] = useState<ISession[]>([]);
    const [loadingSessions, setLoadingSessions] = useState(false);

    useEffect(() => {
        const fetchSessions = async () => {
            if (boundToClient && selectedClient?.id) {
                setLoadingSessions(true);
                try {
                    const sessions = await getFutureSessions(selectedClient.id);
                    setFutureSessions(sessions);
                } catch (error) {
                    console.error('Failed to fetch sessions:', error);
                    setFutureSessions([]);
                } finally {
                    setLoadingSessions(false);
                }
            } else {
                setFutureSessions([]);
                setSelectedSessionId(undefined);
            }
        };
        
        fetchSessions();
    }, [boundToClient, selectedClient?.id]);

    useEffect(() => {
        if (selectedSessionId && futureSessions.length > 0) {
            const selectedSession = futureSessions.find(s => s.id === selectedSessionId);
            if (selectedSession) {
                const sessionDate = new Date(selectedSession.startDatetime || selectedSession.date || '');
                if (!isNaN(sessionDate.getTime())) {
                    setDate(sessionDate);
                    setHasDate(true);
                }
            }
        }
    }, [selectedSessionId, futureSessions]);

    const handleAddTag = () => {
        if (tagInput.trim() && !tags.includes(tagInput.trim())) {
            setTags([...tags, tagInput.trim()]);
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags(tags.filter(t => t !== tagToRemove));
    };

    const handleSubmit = () => {
        if (!title.trim()) {
            alert(translate('tasks_title_required'));
            return;
        }

        onSave({
            title: title.trim(),
            description: description.trim(),
            hasDate,
            start: hasDate ? date : null as any,
            end: hasDate ? date : null as any,
            type: 'task',
            tags,
            id: task?.id,
            original: task?.original,
            client: boundToClient && selectedClient?.id ? selectedClient.id : undefined,
            sessionId: selectedSessionId
        });
    };
    
    const formatSessionOption = (session: ISession) => {
        const sessionDate = new Date(session.startDatetime || session.date || '');
        const dateStr = sessionDate.toLocaleDateString();
        const timeStr = sessionDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const title = session.title || translate('tasks_session_untitled') || 'Untitled Session';
        return `${title} - ${dateStr} ${translate('common_at')} ${timeStr}`;
    };

    return (
        <div className="TaskEditModal_overlay" onClick={onClose}>
            <div className="TaskEditModal" onClick={(e) => e.stopPropagation()}>
                <div className="TaskEditModal_header">
                    <div className="TaskEditModal_title">
                        {task ? translate('tasks_edit') : translate('tasks_add')}
                    </div>
                    <button className="TaskEditModal_close" onClick={onClose}>×</button>
                </div>

                <div className="TaskEditModal_content">
                    <div className="TaskEditModal_field">
                        <label className="TaskEditModal_label">{translate('tasks_field_title')}</label>
                        <input
                            type="text"
                            className="TaskEditModal_input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={translate('tasks_title_placeholder')}
                        />
                    </div>

                    <div className="TaskEditModal_field">
                        <label className="TaskEditModal_label">{translate('tasks_field_description')}</label>
                        <textarea
                            className="TaskEditModal_textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={translate('tasks_description_placeholder')}
                            rows={3}
                        />
                    </div>

                    <div className="TaskEditModal_field">
                        <label className="TaskEditModal_checkbox">
                            <input
                                type="checkbox"
                                checked={hasDate}
                                onChange={(e) => setHasDate(e.target.checked)}
                            />
                            <span>{translate('tasks_has_date')}</span>
                        </label>
                    </div>

                    {selectedClient && (
                        <div className="TaskEditModal_field">
                            <label className="TaskEditModal_checkbox">
                                <input
                                    type="checkbox"
                                    checked={boundToClient}
                                    onChange={(e) => setBoundToClient(e.target.checked)}
                                />
                                <span>{translate('tasks_bound_to_client')}</span>
                            </label>
                        </div>
                    )}

                    {boundToClient && selectedClient && (
                        <div className="TaskEditModal_field">
                            <label className="TaskEditModal_label">{translate('tasks_assign_to_session')}</label>
                            {loadingSessions ? (
                                <div className="TaskEditModal_loading">{translate('common_loading') || 'Loading...'}</div>
                            ) : futureSessions.length > 0 ? (
                                <CustomSelect
                                    className="TaskEditModal_input"
                                    value={selectedSessionId || ''}
                                    onChange={(value) => setSelectedSessionId(value || undefined)}
                                    options={[
                                        { value: '', label: translate('tasks_session_none') || 'None' },
                                        ...futureSessions.map(session => ({
                                            value: session.id || '',
                                            label: formatSessionOption(session)
                                        }))
                                    ]}
                                />
                            ) : (
                                <div className="TaskEditModal_noSessions">{translate('tasks_no_future_sessions')}</div>
                            )}
                        </div>
                    )}

                    {hasDate && (
                        <div className="TaskEditModal_field">
                            <label className="TaskEditModal_label">{translate('tasks_field_datetime')}</label>
                            <input
                                type="datetime-local"
                                className="TaskEditModal_input"
                                value={date.toISOString().slice(0, 16)}
                                onChange={(e) => setDate(new Date(e.target.value))}
                            />
                        </div>
                    )}

                    <div className="TaskEditModal_field">
                        <label className="TaskEditModal_label">{translate('tasks_field_tags')}</label>
                        <div className="TaskEditModal_tagInput">
                            <input
                                type="text"
                                className="TaskEditModal_input"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (handleAddTag(), e.preventDefault())}
                                placeholder={translate('tasks_tag_placeholder')}
                            />
                            <button onClick={handleAddTag} className="TaskEditModal_addTagButton">+</button>
                        </div>
                        {tags.length > 0 && (
                            <div className="TaskEditModal_tags">
                                {tags.map(tag => (
                                    <span key={tag} className="TaskEditModal_tag">
                                        {tag}
                                        <button onClick={() => handleRemoveTag(tag)} className="TaskEditModal_tagRemove">×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="TaskEditModal_footer">
                    <button className="TaskEditModal_button TaskEditModal_button--secondary" onClick={onClose}>
                        {translate('common_cancel')}
                    </button>
                    <button className="TaskEditModal_button TaskEditModal_button--primary" onClick={handleSubmit}>
                        {translate('common_save')}
                    </button>
                    {onDelete && taskId && (
                        <button 
                            className="TaskEditModal_button TaskEditModal_button--delete" 
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                                onClose();
                            }}
                            title={translate('tasks_delete')}
                        >
                            <img src={TrashIcon} alt={translate('tasks_delete') || 'Delete'} className="TaskEditModal_delete_icon" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
